import axios from 'axios';

let csrfToken = '';
let apiBaseUrl = '';
let actionConfirmationHandler = null;
let sessionExpiredHandler = null;

function actionTarget(url, method) {
  return {
    method: String(method || 'GET').toUpperCase(),
    path: new URL(url, apiBaseUrl).pathname,
  };
}

async function getActionConfirmationToken(target) {
  if (!actionConfirmationHandler || !await actionConfirmationHandler(target)) {
    return null;
  }
  const response = await axios.post(
    `${apiBaseUrl}/api/v1/admin/session/action-confirmation`,
    target,
  );
  return response.data.confirmationToken;
}

export function configureAdminApi(baseUrl) {
  apiBaseUrl = baseUrl;
  const apiOrigin = new URL(baseUrl).origin;
  axios.interceptors.request.use((config) => {
    const requestUrl = new URL(config.url, baseUrl);
    if (requestUrl.origin !== apiOrigin) return config;

    config.withCredentials = true;
    if (config.headers) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }
    if (csrfToken && ['delete', 'patch', 'post', 'put'].includes(config.method?.toLowerCase())) {
      config.headers = config.headers || {};
      config.headers['X-Admin-CSRF-Token'] = csrfToken;
    }
    return config;
  });
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      if (
        error.response?.status === 401
        && ['session_expired', 'session_required'].includes(error.response?.data?.code)
      ) {
        sessionExpiredHandler?.();
      }
      if (
        error.response?.status !== 428
        || error.response?.data?.code !== 'admin_action_confirmation_required'
        || !config
        || config.adminConfirmationRetried
      ) {
        throw error;
      }
      const target = actionTarget(config.url, config.method);
      const token = await getActionConfirmationToken(target);
      if (!token) throw error;
      config.adminConfirmationRetried = true;
      config.headers = config.headers || {};
      config.headers['X-Admin-Action-Confirmation'] = token;
      return axios(config);
    },
  );
}

export function setAdminCsrfToken(value) {
  csrfToken = typeof value === 'string' ? value : '';
}

export function clearAdminCsrfToken() {
  csrfToken = '';
}

export function getOperationsOverview() {
  return axios.get(`${apiBaseUrl}/api/v1/admin/operations/overview`);
}

function operationsParams(values) {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== null && value !== undefined && value !== ''),
  );
}

export function getOperationsSpeechCalls(params) {
  return axios.get(`${apiBaseUrl}/api/v1/admin/operations/speech-calls`, {
    params: operationsParams(params),
  });
}

export function getOperationsSpeechCall(id) {
  return axios.get(`${apiBaseUrl}/api/v1/admin/operations/speech-calls/${encodeURIComponent(id)}`);
}

export function getOperationsLogs(params) {
  return axios.get(`${apiBaseUrl}/api/v1/admin/operations/logs`, {
    params: operationsParams(params),
  });
}

export function getUserSpeechUsage(userId) {
  return axios.get(
    `${apiBaseUrl}/api/v1/admin/users/${encodeURIComponent(userId)}/speech-usage`,
  );
}

export function getAdminUsers(search = '') {
  const normalizedSearch = typeof search === 'string' ? search.trim() : '';
  return axios.get(`${apiBaseUrl}/api/admin/users`, {
    params: normalizedSearch ? { search: normalizedSearch } : {},
  });
}

export function getAdminUserSuggestions(search = '') {
  const normalizedSearch = typeof search === 'string' ? search.trim() : '';
  return axios.get(`${apiBaseUrl}/api/admin/users`, {
    params: { search: normalizedSearch, suggest: 'true' },
  });
}

export function addUserDailySpeechCredits(userId, credits) {
  return axios.post(
    `${apiBaseUrl}/api/v1/admin/users/${encodeURIComponent(userId)}/speech-daily-credits`,
    { credits },
  );
}

export function runOperationsControl(path) {
  return axios.post(`${apiBaseUrl}${path}`);
}

export function setAdminActionConfirmationHandler(handler) {
  actionConfirmationHandler = typeof handler === 'function' ? handler : null;
}

export function setAdminSessionExpiredHandler(handler) {
  sessionExpiredHandler = typeof handler === 'function' ? handler : null;
}

export function adminFetch(url, options = {}) {
  const headers = new Headers(options.headers || {});
  headers.delete('Authorization');
  if (csrfToken && ['DELETE', 'PATCH', 'POST', 'PUT'].includes(
    String(options.method || 'GET').toUpperCase(),
  )) {
    headers.set('X-Admin-CSRF-Token', csrfToken);
  }
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  }).then(async (response) => {
    if (response.status === 401) {
      const body = await response.clone().json().catch(() => null);
      if (['session_expired', 'session_required'].includes(body?.code)) {
        sessionExpiredHandler?.();
      }
    }
    if (response.status !== 428 || options.adminConfirmationRetried) return response;
    const body = await response.clone().json().catch(() => null);
    if (body?.code !== 'admin_action_confirmation_required') return response;
    const target = actionTarget(url, options.method);
    const token = await getActionConfirmationToken(target);
    if (!token) return response;
    headers.set('X-Admin-Action-Confirmation', token);
    return fetch(url, {
      ...options,
      adminConfirmationRetried: true,
      credentials: 'include',
      headers,
    });
  });
}
